/**
 * Who is signed in, and what the app should do when nobody is.
 *
 * **The thing this gets right that a naive version gets wrong: demo mode.**
 * In mock mode the API resolves an unauthenticated request to a seeded demo
 * user and says so with `demo: true` on `/v1/auth/me`. If the app treated "no
 * session" as "show the login wall", `pnpm dev` on a fresh clone would open on a
 * login form with no account to log into, and the promise that the whole product
 * runs with zero infrastructure would be false. So there are three states, not
 * two:
 *
 *   loading         — the first `/v1/auth/me` has not answered yet
 *   authenticated   — a real session, real user
 *   demo            — no session, but the server handed us an identity anyway
 *   anonymous       — no session and no demo user: the login wall is correct
 *
 * A 401 from any request anywhere collapses the state to `anonymous` and clears
 * the stored token, because a session that expired mid-poll must not leave the
 * UI showing another user's numbers.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { JSX, ReactNode } from 'react';
import { api, setUnauthenticatedHandler, type AuthSuccess, type User } from './api.js';
import { confirmDevice, setSessionToken } from './credentials.js';
import { navigate } from './router.js';

export type SessionStatus = 'loading' | 'authenticated' | 'demo' | 'anonymous';

export interface SessionState {
  readonly status: SessionStatus;
  readonly user: User | null;
  /**
   * Whether to draw the admin affordances. Never a permission — the server
   * gates every admin endpoint on its own list and answers 404 to anyone else,
   * so this only decides whether a link is rendered.
   *
   * Carried by `/v1/auth/me` and by a successful login, so an admin who has
   * just signed in does not have to reload to see it.
   */
  readonly admin: boolean;
  /**
   * Set when a login or signup succeeded but the device is over the plan's
   * market-data cap. Held here rather than in the login screen because the user
   * has already been navigated away by the time they could read it.
   */
  readonly notice: string | null;
  readonly signup: (body: SignupInput) => Promise<void>;
  readonly login: (email: string, password: string) => Promise<void>;
  readonly logout: () => Promise<void>;
  readonly refresh: () => Promise<void>;
  readonly dismissNotice: () => void;
}

export interface SignupInput {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  subscriberCategory?: 'non_professional' | 'professional';
}

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }): JSX.Element {
  const [status, setStatus] = useState<SessionStatus>('loading');
  const [user, setUser] = useState<User | null>(null);
  const [admin, setAdmin] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const me = await api.me();
      setUser(me.user);
      setAdmin(me.admin === true);
      setStatus(me.demo ? 'demo' : 'authenticated');
    } catch {
      // Any failure here — 401, network, a server that is not up yet — means we
      // cannot claim an identity. `anonymous` is the safe answer; the screens
      // that need a session will send the user to sign in.
      setUser(null);
      setAdmin(false);
      setStatus('anonymous');
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  // A 401 on any request, including background polls, ends the session here.
  useEffect(() => {
    setUnauthenticatedHandler(() => {
      setSessionToken(null);
      setUser(null);
      setAdmin(false);
      setStatus('anonymous');
    });
    return () => setUnauthenticatedHandler(null);
  }, []);

  const accept = useCallback((result: AuthSuccess) => {
    // A no-op on the web, where the token stays in the httpOnly cookie the
    // server just set. See credentials.ts for why that asymmetry exists.
    setSessionToken(result.token);
    // The server has now registered this device, so requests may start
    // announcing it. Before this point they must not — see credentials.ts.
    confirmDevice(result.deviceId);
    setUser(result.user);
    setAdmin(result.admin === true);
    setStatus('authenticated');
    setNotice(result.notice ?? null);
  }, []);

  const signup = useCallback(async (body: SignupInput) => {
    accept(await api.signup(body));
  }, [accept]);

  const login = useCallback(async (email: string, password: string) => {
    accept(await api.login({ email, password }));
  }, [accept]);

  const logout = useCallback(async () => {
    // Order matters: tell the server first, because clearing the local token
    // first would leave the server-side session alive with nothing able to
    // revoke it. If the call fails the session is still revoked locally — a
    // user who clicked "sign out" must end up signed out of this device
    // regardless of what the network did.
    try {
      await api.logout();
    } finally {
      setSessionToken(null);
      setUser(null);
      setAdmin(false);
      setStatus('anonymous');
      setNotice(null);
      navigate('/login');
    }
  }, []);

  const value = useMemo<SessionState>(() => ({
    status, user, admin, notice, signup, login, logout, refresh,
    dismissNotice: () => setNotice(null),
  }), [status, user, admin, notice, signup, login, logout, refresh]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside <SessionProvider>');
  return ctx;
}

/** True when there is an identity to work with, real or seeded. */
export function hasIdentity(status: SessionStatus): boolean {
  return status === 'authenticated' || status === 'demo';
}
